import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Organization, AuthContextType } from '../types/auth';
import { BackofficeLoginRequest } from '../api/BackofficeApi';
import { authAPI } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing auth data on app start
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');
    const orgData = localStorage.getItem('organization');

    if (token && userData && orgData) {
      try {
        setUser(JSON.parse(userData));
        setOrganization(JSON.parse(orgData));
      } catch (error) {
        console.error('Error parsing stored auth data:', error);
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  const isAdmin = Boolean(organization?.permissions?.includes('ADMIN'));

  const login = async (credentials: BackofficeLoginRequest) => {
    try {
      setLoading(true);
      const response = await authAPI.login(credentials);
      
      const userData: User = {
        organization: response.organization.name,
        username: credentials.username,
        authorization: response.token
      };

      setUser(userData);
      setOrganization(response.organization);
      
      // Store in localStorage
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('organization', JSON.stringify(response.organization));
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setOrganization(null);
    localStorage.clear();
  };

  const value: AuthContextType = {
    user,
    organization,
    isAuthenticated: !!user,
    isAdmin,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
